import type { ApiResponse, CommissionStats, CommissionStatsFilters } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

const fetchCommissionStats = async (filters: CommissionStatsFilters): Promise<CommissionStats> => {
  // Clean undefined and empty values
  const params = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'string' && value === '') return false;
      return true;
    })
  );

  const { data } = await api.get<ApiResponse<CommissionStats>>(
    '/dashboard/affiliates/commissions/stats',
    { params }
  );

  if (!data.success || !data.data) {
    throw new Error(data.message || 'Failed to fetch commission statistics');
  }

  return data.data;
};

export const useCommissionStatsQuery = (filters: CommissionStatsFilters) => {
  // Create a stable query key by sorting affiliate IDs
  const queryKeyFilters = {
    ...filters,
    affiliateIds: filters.affiliateIds ? [...filters.affiliateIds].sort().join(',') : undefined,
  };

  return useQuery<CommissionStats, Error>({
    queryKey: ['commissions', 'stats', queryKeyFilters],
    queryFn: () => fetchCommissionStats(filters),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
